import {
  brtDateKey,
  formatDayMonth,
  formatTimeOfDay,
} from "../../../utils/directorFormat";

// Textos do estado dos dados da Diretoria (spec `director/data-freshness`). Só apresentação: o
// estado, a idade e o motivo chegam prontos de `GET /director/refresh-status`.

export const FRESHNESS_LABELS = {
  fresh: "Em dia",
  stale: "Atrasado",
  blocked: "Bloqueado",
  updating: "Atualizando",
};

// Reserva para quando o BE não manda `reasonText` (o BE é a fonte da frase).
const REASON_FALLBACKS = {
  director_publish_blocked: "a validação dos dados não passou",
  partial_coverage_below_threshold: "a cobertura dos títulos ficou abaixo do mínimo",
  unmapped_cost_center: "há centros de custo sem mapeamento no portfólio",
  source_rate_limited: "a cota diária do Sienge acabou",
  sienge_quota_blocked: "a cota diária do Sienge acabou",
  director_source_error: "o Sienge não respondeu",
  landing_scope_mismatch: "a extração de produção não tem todos os centros do portfólio",
  no_prod_extraction: "não há extração de produção concluída",
};

const ORIGIN_LABELS = {
  schedule: "agendamento",
  button: "botão",
  work_run: "obra",
};

export const originLabel = (origin) => ORIGIN_LABELS[origin] || "outra origem";

const JOB_STATUS_LABELS = {
  queued: "Na fila",
  running: "Rodando",
  succeeded: "Publicado",
  failed: "Falhou",
  blocked: "Bloqueado",
  skipped: "Pulado",
};

export const jobStatusLabel = (status) => JOB_STATUS_LABELS[status] || "Estado desconhecido";

const SKIP_REASON_LABELS = {
  already_completed_today: "já atualizada hoje",
  no_prod_extraction: "sem extração de produção",
  sienge_quota_blocked: "cota do Sienge esgotada",
  director_mode_disabled: "atualização desligada",
  skipped_demo_fixture: "dados demonstrativos",
};

export const skipReasonLabel = (code) => SKIP_REASON_LABELS[code] || null;

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// Idade dos dados em dias. Prefere a do BE; sem ela, conta dias civis em Brasília.
export const dataAgeDays = (status, now = new Date()) => {
  if (Number.isInteger(status?.ageDays) && status.ageDays >= 0) return status.ageDays;
  const from = brtDateKey(status?.dataAsOf);
  const to = brtDateKey(now);
  if (!from || !to) return null;
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
  return days >= 0 ? days : null;
};

export const ageText = (days) => {
  if (!Number.isInteger(days)) return null;
  if (days === 0) return "hoje";
  return `há ${plural(days, "dia", "dias")}`;
};

// "Dados de 21/10 às 20:22"; "de hoje" quando é o mesmo dia em Brasília.
export const dataDateText = (status, now = new Date()) => {
  const at = status?.dataAsOf;
  const day = formatDayMonth(at);
  if (!day) return "Sem dados publicados";
  const time = formatTimeOfDay(at);
  const sameDay = brtDateKey(at) === brtDateKey(now);
  return `Dados de ${sameDay ? "hoje" : day}${time ? ` às ${time}` : ""}`;
};

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

// Motivo em uma frase: os centros sem mapeamento com a contagem quando o diagnóstico veio
// (só quem lê a governança recebe a lista), senão a frase do BE, senão a reserva local.
const reasonDetail = (status) => {
  const unmapped = status?.diagnostic?.unmappedCostCenters;
  if (Array.isArray(unmapped) && unmapped.length > 0) {
    return `${plural(unmapped.length, "centro de custo", "centros de custo")} sem mapeamento`;
  }
  const text = typeof status?.lastAttempt?.reasonText === "string" ? status.lastAttempt.reasonText.trim() : "";
  if (text) return lowerFirst(text.replace(/\.$/, ""));
  return REASON_FALLBACKS[status?.lastAttempt?.reasonCode] || "motivo não informado";
};

// "Atualização de hoje bloqueada: 16 centros de custo sem mapeamento".
export const blockedReasonSentence = (status, now = new Date()) => {
  const attempt = status?.lastAttempt;
  if (!attempt) return null;
  const day = attempt.at && brtDateKey(attempt.at) !== brtDateKey(now)
    ? `de ${formatDayMonth(attempt.at)}`
    : "de hoje";
  const verb = attempt.status === "failed" ? "falhou" : "bloqueada";
  return `Atualização ${day} ${verb}: ${reasonDetail(status)}`;
};

const ACTIVE_JOB_TEXT = {
  queued: "na fila",
  running: "rodando",
};

// Linha do cabeçalho: data dos dados, idade (Atrasado e Bloqueado) e a posição usada nas contas.
export const freshnessLine = (status, now = new Date()) => {
  if (!status) return null;
  const parts = [dataDateText(status, now)];
  const lagging = status.state === "stale" || status.state === "blocked";
  if (lagging) {
    const age = ageText(dataAgeDays(status, now));
    if (age) parts.push(age);
    const reference = formatDayMonth(status.referenceDate || status.dataBusinessDate);
    if (reference) parts.push(`posição em ${reference}`);
  }
  if (status.state === "updating") {
    const job = ACTIVE_JOB_TEXT[status.activeJob?.status];
    if (job) parts.push(`atualização ${job}`);
  }
  return parts.join(" · ");
};
